import React from 'react'
import { DashboardLayout, Navigation } from '@toolpad/core'

import { ReactRouterAppProvider } from '@toolpad/core/react-router' // Import the ReactRouterAppProvider
import { useNavigate } from 'react-router'

import { Stack, Typography, Menu, MenuItem, Divider } from '@mui/material'
import { createTheme } from '@mui/material/styles'
import { backgroundColors, brandColors, textColors } from '../Color/Color'
import { useAppSelector } from '../../../redux/store'
import { selectUser, selectUserRole } from '../../../redux/auth/selector'

import FormatListBulletedRoundedIcon from '@mui/icons-material/FormatListBulletedRounded'
import EventRoundedIcon from '@mui/icons-material/EventRounded'
import DashboardRoundedIcon from '@mui/icons-material/DashboardRounded'
import GroupsRoundedIcon from '@mui/icons-material/GroupsRounded'
import LocalMallRoundedIcon from '@mui/icons-material/LocalMallRounded'
import AccountBalanceRoundedIcon from '@mui/icons-material/AccountBalanceRounded'
import DonutSmallRoundedIcon from '@mui/icons-material/DonutSmallRounded'
import CircleOutlinedIcon from '@mui/icons-material/CircleOutlined'
import MenuBookRoundedIcon from '@mui/icons-material/MenuBookRounded'
import GradingRoundedIcon from '@mui/icons-material/GradingRounded'
import LOGO from '../../../assets/images/Logo.png'

import { fontSizes } from '../Typography/TypographyStyles'
import { Box } from '@mui/system'

import { theme } from '../../../theme/ThemeProvider'
import PageContent from './PageContent'
import FloatingButton from '../FloatingButton/FloatingButton'

const customTheme = createTheme({
  ...theme,
  components: {
    MuiAppBar: {
      styleOverrides: {
        root: {
          background: backgroundColors.purpleGradient, // AppBar background
          color: backgroundColors.white, // AppBar text/icon color
          '& .MuiIconButton-root': {
            color: backgroundColors.white, // AppBar icon color
          },
        },
      },
    },

    MuiDrawer: {
      styleOverrides: {
        paper: {
          display: 'flex',
          flexDirection: 'column',
          // height: '100vh',
          color: textColors.purpleTelus,
          '& .MuiList-root': {
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
          },

          '& .MuiListItemButton-root': {
            color: textColors.purpleTelus,
            '&:hover': {
              backgroundColor: brandColors.greenTusk,
              color: textColors.purpleTelus,
            },
          },
        },
      },
    },
  },
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 600,
      lg: 1200,
      xl: 1536,
    },
  },
})

function getNavigation(role: string): Navigation {
  const baseNav: Navigation = [
    {
      kind: 'header',
      title: 'DASHBOARD',
    },
    {
      segment: 'overview',
      title: 'OVERVIEW',
      icon: <DashboardRoundedIcon sx={{ color: textColors.purpleTelus, fontSize: fontSizes.size20 }} />,
    },
    {
      segment: role === 'admin' ? 'events' : 'events',
      title: 'EVENTS',
      icon: <EventRoundedIcon sx={{ color: textColors.purpleTelus, fontSize: fontSizes.size20 }} />,
      ...(role === 'admin'
        ? {
            children: [
              {
                segment: '',
                title: 'VIEW EVENTS',
                icon: (
                  <CircleOutlinedIcon
                    sx={{ color: textColors.purpleTelus, fontSize: fontSizes.size14 }}
                  />
                ),
              },
              {
                segment: 'manage-events',
                title: 'MANAGE EVENTS',
                icon: (
                  <CircleOutlinedIcon
                    sx={{ color: textColors.purpleTelus, fontSize: fontSizes.size14 }}
                  />
                ),
              },
            ],
          }
        : {}),
    },
    {
      segment: 'tasks',
      title: 'TASKS',
      icon: (
        <FormatListBulletedRoundedIcon
          sx={{ color: textColors.purpleTelus, fontSize: fontSizes.size20 }}
        />
      ),
    },
    {
      segment: 'orders',
      title: 'ORDERS',
      icon: <LocalMallRoundedIcon sx={{ color: textColors.purpleTelus, fontSize: fontSizes.size20 }} />,
    },
  ]

  if (role === 'admin') {
    baseNav.push(
      {
        kind: 'divider',
      },
      {
        kind: 'header',
        title: 'ADMINISTRATION',
      },
      {
        segment: 'team-roster',
        title: 'TEAM ROSTER',
        icon: <GroupsRoundedIcon sx={{ color: textColors.purpleTelus, fontSize: fontSizes.size20 }} />,
      },
      {
        segment: 'expenses',
        title: 'EXPENSES',
        icon: (
          <AccountBalanceRoundedIcon
            sx={{ color: textColors.purpleTelus, fontSize: fontSizes.size20 }}
          />
        ),
      },
      {
        segment: 'reports',
        title: 'REPORTS',
        icon: (
          <DonutSmallRoundedIcon sx={{ color: textColors.purpleTelus, fontSize: fontSizes.size20 }} />
        ),
        children: [
          {
            segment: 'event',
            title: 'EVENTS',
            icon: (
              <CircleOutlinedIcon sx={{ color: textColors.purpleTelus, fontSize: fontSizes.size14 }} />
            ),
          },
          // Uncomment the following line if you want to include task reports
          // {
          //   segment: 'task',
          //   title: 'TASK',
          //   icon: (
          //     <CircleOutlinedIcon sx={{ color: textColors.purpleTelus, fontSize: fontSizes.size14 }} />
          //   ),
          // },
        ],
      },
    )
  }

  // Removed navigation items for User Guide and Help & Support

  return baseNav
}

function CustomAppTitle() {
  return (
    <Stack direction='row' alignItems='center' spacing={2}>
      <Box component='img' src={LOGO} alt='logo' height={50} alignItems='center' display='flex' />
    </Stack>
  )
}

function ToolbarUser() {
  const authUser = useAppSelector(selectUser)
  const navigate = useNavigate()
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null)

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const handleSettingsClick = () => {
    navigate('/settings')
    handleClose()
  }

  const handleUserGuideClick = () => {
    navigate('/user-guide')
    handleClose()
  }

  const handleFeedbackClick = () => {
    navigate('/support')
    handleClose()
  }

  return (
    <>
      <Box
        component='img'
        src={authUser.googleUserInfo.picture}
        alt='user'
        height={40}
        width={40}
        onClick={handleClick}
        sx={{
          borderRadius: '50%',
          objectFit: 'cover',
          cursor: 'pointer',
          '&:hover': {
            opacity: 0.8,
          },
        }}
      />

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        onClick={handleClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        slotProps={{
          paper: {
            elevation: 0,
            sx: {
              overflow: 'visible',
              filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
              mt: 2,
              '& .MuiAvatar-root': {
                width: 32,
                height: 32,
                ml: 1,
                mr: 1,
              },
              '&::before': {
                content: '""',
                display: 'block',
                position: 'absolute',
                top: 0,
                right: 15,
                width: 10,
                height: 10,
                bgcolor: 'background.paper',
                transform: 'translateY(-50%) rotate(45deg)',
                zIndex: 0,
              },
            },
          },
        }}
      >
        <MenuItem
          sx={{ borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}
          onClick={handleSettingsClick}
        >
          <Stack direction='row' spacing={1} alignItems='center'>
            <Box
              component='img'
              src={authUser.googleUserInfo.picture}
              alt='user'
              height={45}
              width={45}
              sx={{
                borderRadius: '50%',
                objectFit: 'cover',
                cursor: 'pointer',
              }}
            />
            <Stack>
              <Typography sx={{ fontWeight: 'bold', fontSize: fontSizes.size14 }}>
                {authUser.teamMemberInfo.firstName} {authUser.teamMemberInfo.lastName}
              </Typography>
              <Typography variant='body2' sx={{ opacity: 0.7 }}>
                {authUser.googleUserInfo.email}
              </Typography>
              <Typography variant='body2' sx={{ opacity: 0.7 }}>
                {authUser.teamMemberInfo.jobProfile}
              </Typography>
            </Stack>
          </Stack>
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleUserGuideClick}>
          <Stack direction='row' spacing={1} alignItems='center'>
            <MenuBookRoundedIcon fontSize='small' />
            <Typography>User Guide</Typography>
          </Stack>
        </MenuItem>
        <MenuItem onClick={handleFeedbackClick}>
          <Stack direction='row' spacing={1} alignItems='center'>
            <GradingRoundedIcon fontSize='small' />
            <Typography>Feedback</Typography>
          </Stack>
        </MenuItem>
      </Menu>
    </>
  )
}

interface DashboardProps {
  children: React.ReactNode
}

const Dashboard: React.FC<DashboardProps> = ({ children }) => {
  const userRole = useAppSelector(selectUserRole)
  const navigation = getNavigation(userRole)

  return (
    <ReactRouterAppProvider navigation={navigation} theme={customTheme}>
      <DashboardLayout
        slots={{
          appTitle: CustomAppTitle,
          toolbarAccount: ToolbarUser,
        }}
        sidebarExpandedWidth={240}
      >
        <PageContent components={children} />
        <FloatingButton />
      </DashboardLayout>
    </ReactRouterAppProvider>
  )
}

export default Dashboard
